const SUPABASE_VERDICT_BUCKET_SEGMENT = '/storage/v1/object/public/verdict-images/';

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeUrl(value) {
  return isNonEmptyString(value) ? value.trim() : null;
}

function collectObjects(input) {
  if (!input || typeof input !== 'object') return [];
  const objects = [input];
  if (input.verdict_data && typeof input.verdict_data === 'object') objects.push(input.verdict_data);
  if (input.final_verdict && typeof input.final_verdict === 'object') objects.push(input.final_verdict);
  if (input.trial && typeof input.trial === 'object') {
    objects.push(input.trial);
    if (input.trial.verdict_data && typeof input.trial.verdict_data === 'object') objects.push(input.trial.verdict_data);
  }
  return objects;
}

function getEntityIds(input) {
  const objects = collectObjects(input);
  const firstPresent = (...values) => values.find((value) => isNonEmptyString(value) || typeof value === 'number');
  const decisionId = firstPresent(
    input?.decision_id,
    input?.decisionId,
    ...objects.map((obj) => obj?.decision_id),
  );
  const trialId = firstPresent(
    input?.trial_id,
    input?.trialId,
    input?.trial?.id,
    input?.id,
    decisionId,
    ...objects.map((obj) => obj?.trial_id),
  );
  return {
    trialId: trialId != null ? String(trialId).trim() : null,
    decisionId: decisionId != null ? String(decisionId).trim() : null,
  };
}

function classifyAssetType(url) {
  const normalized = String(url || '').toLowerCase();
  if (normalized.endsWith('.svg') || normalized.includes('.svg?')) return 'svg';
  if (normalized.endsWith('.png') || normalized.includes('.png?')) return 'png';
  if (normalized.endsWith('.jpg') || normalized.endsWith('.jpeg') || normalized.includes('.jpg?') || normalized.includes('.jpeg?')) return 'jpg';
  if (normalized.endsWith('.webp') || normalized.includes('.webp?')) return 'webp';
  return 'other';
}

function addCandidate(candidates, seen, url, source) {
  const normalized = normalizeUrl(url);
  if (!normalized || seen.has(normalized)) return;
  seen.add(normalized);
  candidates.push({
    url: normalized,
    type: classifyAssetType(normalized),
    source,
  });
}

export function deriveVerdictCardSvgUrl(assetUrl, trialId) {
  const normalizedUrl = normalizeUrl(assetUrl);
  const normalizedTrialId = normalizeUrl(trialId);
  if (!normalizedUrl || !normalizedTrialId) return null;
  if (!normalizedUrl.includes(SUPABASE_VERDICT_BUCKET_SEGMENT)) return null;
  try {
    const parsed = new URL(normalizedUrl);
    return `${parsed.origin}${SUPABASE_VERDICT_BUCKET_SEGMENT}${normalizedTrialId}/verdict-card.svg`;
  } catch {
    return null;
  }
}

export function getCanonicalRenderedVerdictAsset(trialOrVerdictData) {
  const objects = collectObjects(trialOrVerdictData);
  const { trialId, decisionId } = getEntityIds(trialOrVerdictData);
  const candidates = [];
  const seen = new Set();

  const explicitShareCardUrls = [];
  const explicitPremiumShareCardUrls = [];
  const otherSvgUrls = [];
  const rasterUrls = [];

  for (const obj of objects) {
    const imageSource = String(obj?.image_source || '').toLowerCase();
    const premiumShareCardUrl = normalizeUrl(obj?.composed_share_card_url);
    const shareCardUrl = normalizeUrl(obj?.share_card_url);
    const isDailyAssetSource = Boolean(
      decisionId
      || obj?.decision_id
      || obj?.daily_question_id
    );
    if (premiumShareCardUrl && (imageSource === 'ai_primary' || isDailyAssetSource)) {
      explicitPremiumShareCardUrls.push(premiumShareCardUrl);
    }
    if (shareCardUrl) explicitShareCardUrls.push(shareCardUrl);

    const urls = [
      normalizeUrl(obj?.final_image_url),
      normalizeUrl(obj?.verdict_image_url),
      normalizeUrl(obj?.image_url),
      normalizeUrl(obj?.aiImage),
      normalizeUrl(obj?.ai_image),
      normalizeUrl(obj?.background_image_url),
      normalizeUrl(obj?.meme_image_url),
      normalizeUrl(obj?.imageUrl),
    ].filter(Boolean);

    for (const url of urls) {
      if (classifyAssetType(url) === 'svg') otherSvgUrls.push(url);
      else rasterUrls.push(url);
    }
  }

  explicitPremiumShareCardUrls.forEach((url) => addCandidate(candidates, seen, url, 'explicit_premium_share_card_url'));
  explicitShareCardUrls.forEach((url) => addCandidate(candidates, seen, url, 'explicit_share_card_url'));

  const derivationSeeds = [...explicitShareCardUrls, ...otherSvgUrls, ...rasterUrls];
  if (trialId) {
    for (const seed of derivationSeeds) {
      const derivedUrl = deriveVerdictCardSvgUrl(seed, trialId);
      if (derivedUrl) {
        addCandidate(candidates, seen, derivedUrl, 'derived_bucket_svg');
        break;
      }
    }
  }

  otherSvgUrls.forEach((url) => addCandidate(candidates, seen, url, 'other_svg_asset'));
  rasterUrls.forEach((url) => addCandidate(candidates, seen, url, 'raster_fallback'));

  const primary = candidates[0] || null;
  return {
    url: primary?.url || null,
    type: primary?.type || null,
    source: primary?.source || null,
    candidates,
    trialId,
    decisionId,
  };
}

export function getCanonicalTrialDetailVerdictUrl(trialOrVerdictData) {
  const asset = getCanonicalRenderedVerdictAsset(trialOrVerdictData);
  const verdictData =
    trialOrVerdictData?.verdict_data && typeof trialOrVerdictData.verdict_data === 'object'
      ? trialOrVerdictData.verdict_data
      : (trialOrVerdictData && typeof trialOrVerdictData === 'object' ? trialOrVerdictData : null);
  const imageSource = String(verdictData?.image_source || '').toLowerCase();
  const baseUrl = asset?.url || null;
  if (!baseUrl) {
    return {
      url: null,
      source: asset?.source || null,
      imageSource,
    };
  }
  if (imageSource !== 'ai_primary') {
    return {
      url: baseUrl,
      source: asset?.source || null,
      imageSource,
    };
  }
  const revisionToken = verdictData?.background_image_url
    ? encodeURIComponent(String(verdictData.background_image_url))
    : 'premium';
  return {
    url: `${baseUrl}${String(baseUrl).includes('?') ? '&' : '?'}premium_rev=${revisionToken}`,
    source: asset?.source || null,
    imageSource,
  };
}

export function getCanonicalTrialVerdictText(row) {
  const vd = row?.verdict_data && typeof row.verdict_data === 'object' ? row.verdict_data : null;
  const captionFull =
    vd?.subverdict_line ??
    vd?.verdict_line_full ??
    vd?.verdict_line ??
    vd?.share_line ??
    vd?.full_verdict_line ??
    vd?.punchline ??
    row?.subverdict_line ??
    row?.verdict_line_full ??
    row?.verdict_line ??
    row?.share_line ??
    row?.full_verdict_line ??
    row?.internet_punchline ??
    row?.roast_punchline ??
    row?.sports_punchline ??
    null;
  const captionShort =
    vd?.overlay_line ??
    vd?.verdict_line_short ??
    row?.overlay_line ??
    row?.verdict_line_short ??
    null;
  return {
    captionFull,
    captionShort,
  };
}

export function deriveTrialVerdictRenderData(row) {
  const image = getCanonicalTrialDetailVerdictUrl(row);
  const { captionFull, captionShort } = getCanonicalTrialVerdictText(row);
  return {
    imageUrl: image.url,
    imageSource: image.source,
    imagePath: image.imageSource,
    captionFull,
    captionShort,
  };
}
